import request from '@/lib/request';
import { dayUnixValue, chartThemeColors } from '@/constant';
import store from '@/store';
import { formatDataSourceHandle, initColumns, upgradeFieldGroupRelationId } from '@/lib/utils';
import dayjs from 'dayjs';
import reporter from '@/lib/reporter';
import { sortBy } from 'lodash';
import { MessageBox } from 'element-ui';

export async function fetchCoursewareList(params) {
  const result = await request.post('/v4/case_chart/simple_list', params);

  result.data = (result.data || []).map(item => {
    return {
      _id: item.id,
      _source: item
    };
  });

  return result;
}

export function getOwnerCourseCount() {
  return request.get('/v4/case_chart/empty_case');
}

// 获取课题详情
let curTaskCourseId = '';
export async function getCoursewareDetail(id, options = {}) {
  const { changeTaskStatus = () => {} } = options;

  let [courseDetail, courseFields] = await Promise.all([
    request.post(`/nlp/case/chart/list/${id}`, {}),
    new Promise(resolve =>
      getFields(id)
        .then(resolve)
        .catch(() => resolve([]))
    )
  ]);

  let source = courseDetail._source;

  curTaskCourseId = id;
  if (source.task_id) {
    changeTaskStatus(true, () => {
      if (curTaskCourseId === id) {
        curTaskCourseId = '';
      }
    });
    // eslint-disable-next-line no-constant-condition
    while (true) {
      await new Promise(resolve => setTimeout(resolve, 3000));
      if (curTaskCourseId !== id) {
        throw new Error('task polling cancel');
      }
      const taskResult = await request.get(`/nlp/task/${source.task_id}`);
      if (curTaskCourseId !== id) {
        throw new Error('task polling cancel');
      }
      if (!taskResult || taskResult.status === 'error') {
        MessageBox.alert('课题初始化异常，请重新创建课题', '创建异常', {
          type: 'error',
          cancelButtonText: '返回首页',
          showConfirmButton: false,
          callback: () => {
            location.href = `${location.origin}${location.pathname}`;
          }
        });
        throw new Error('task polling cancel');
      }
      if (taskResult.status === 'done') {
        break;
      }
    }
    courseDetail = await request.post(`/nlp/case/chart/list/${id}`, {});
    source = courseDetail._source;
    changeTaskStatus(false);
    source.task_id && delete source.task_id;
  }

  const conditionArr = JSON.parse(source.condition || '[]').map(item => {
    const info = { ...item };
    if (info.data) {
      delete info.data;
    }
    return info;
  });

  // 需要转成showValue
  const types = ['审理程序', '地域及法院'];
  for (const type of types) {
    const obj = conditionArr.find(i => i.name === type);
    if (obj && obj.value.length && !obj.showValue) {
      obj.showValue = [...obj.value];
    }
  }

  const instrumentType = source.instrumentType || '裁判文书';

  let courseRefresh = source.refresh;

  const chart = (source.chart || []).map(chartItem => {
    let { rows = [], statistics = [], relationFilterVal, relationFilterName, statisticFieldArr } = chartItem;
    if (statistics === undefined) {
      statistics = [];
    }

    if (!relationFilterVal) {
      relationFilterVal = 'case';
    }

    /**
     * 旧版研究点没有 relationFilterName 则默认初始化并设置课题 refresh 为true
     * 旧版的relationFilterVal只有两个选项【case和defendantNames】
     */
    if (!relationFilterName) {
      courseRefresh = true;
      relationFilterName = relationFilterVal === 'case' ? '以案件为单位' : '以被告人为单位';
    }

    if (chartItem.type !== 'tableAnalysisChart') {
      // 过滤掉旧版的案件数和标签数
      statistics = statistics.filter(statisticItem => !statisticItem.fieldDetail.isDefaultStatistic);

      // 兼容旧版结构
      if (!chartItem.statisticFieldArr) {
        statisticFieldArr = [];
        [...rows, ...statistics].forEach((statisticFieldItem, statisticFieldIndex) => {
          const isRowItem = statisticFieldIndex < rows.length;
          if (isRowItem) {
            statisticFieldArr.push({ mode: 'rowMode', info: { fieldDetail: statisticFieldItem } });
          } else {
            statisticFieldArr.push({ mode: 'statisticMode', info: statisticFieldItem });
          }
        });
      }
    }

    let size = chartItem.size;
    if (!size) {
      size = (chartItem.x || 12) > 12 ? 'large' : 'middle';
    }

    if (!['tableAnalysisChart', 'numChart'].includes(chartItem.type) && !chartItem.colors) {
      const themeKey = chartItem.themeKey || 'colors';
      const themeKeyIdx = [
        { key: 'colors', index: 0 },
        { key: 'solid', index: chartThemeColors.length - 3 },
        { key: 'blackWhite', index: chartThemeColors.length - 2 },
        { key: 'only', index: chartThemeColors.length - 1 }
      ];
      let themeIndex = themeKeyIdx.findIndex(o => o.key === themeKey);
      if (themeIndex === -1) {
        themeIndex = 0;
      }
      chartItem.colors = chartThemeColors[themeIndex];
    }

    return {
      ...chartItem,
      fields: upgradeFieldGroupRelationId(chartItem.fields, courseFields),
      size,
      statistics,
      relationFilterVal,
      relationFilterName,
      statisticFieldArr
    };
  });

  store.dispatch('updCoursewareVersion', courseDetail);
  if (courseDetail._source.timestamp_version) {
    delete courseDetail._source.timestamp_version;
  }

  return {
    ...courseDetail,
    _source: {
      ...source,
      fields: upgradeFieldGroupRelationId(source.fields, courseFields),
      condition: JSON.stringify(conditionArr),
      instrumentType,
      refresh: courseRefresh,
      chart,
      course_size: source.course_size || 0,
      totalCount: source.totalCount || 0,
      currentCaseCount: source.currentCaseCount || 0
    }
  };
}

// 新建课题
export function createCourseware(params) {
  return request.post('/nlp/es/save', params);
}

// 保存课题
export async function saveCourseware(id, params, options) {
  const { isUpdFields = false } = options || {};

  if (params.type === '示例课题' && !store.getters.isAdmin) {
    console.error('示例课题保存失败（非管理员）');
    return;
  }

  if (params.limit === 'read') {
    console.error('保存失败，没有操作权限');
    return;
  }

  if (params.expirationTime && dayjs().isAfter(dayjs(params.expirationTime))) {
    throw new Error('保存失败，当前课题已过期');
  }

  const result = await request.post('/nlp/case/update/case/chart/data', {
    chart_id: id,
    body: params
  });

  if (store.getters.isCheckingVersion) {
    store.dispatch('updCoursewareVersion', { _id: id, _source: { timestamp_version: result.timestamp_version } });
  }

  if (isUpdFields) {
    await codeRunAddQuery(id, params);
  }

  return result;
}

export async function delCourseware(id, params) {
  if (params.type === '示例课题' && !store.getters.isAdmin) {
    console.error('示例课题删除失败（非管理员）');
    return;
  }
  if (params.limit === 'read') {
    console.error('保存失败，没有操作权限');
    return;
  }
  const result = await request.post('/nlp/case/update/case/chart/data', {
    chart_id: id,
    body: params
  });

  return result;
}

// 添加系统标签标签时跑数据
async function codeRunAddQuery(id, params) {
  try {
    const body = {
      course_id: id,
      field_dict: params.fields.map(fieldItem => {
        return {
          _id: fieldItem._id,
          field_name: fieldItem.field_name
        };
      })
    };
    await request.post('/nlp/case/label/add', body);
  } catch (err) {
    console.error(err);
  }
}

// 生成课题对应索引
export function saveIndexForCourse(params) {
  return request.post('/nlp/label/index', params);
}

// 获取所有的案由对应关系表
export function getCourseCrime(params) {
  return request.post('/sends/configure/search', {
    body: params
  });
}

// 案件列表搜索
export async function getCaseCount(params) {
  const result = await request.post('/nlp/case/count', params);
  return result.hits;
}

export function fetchAdministrativeCount(params) {
  return request.post('/v4/administrative/count', params);
}

export function clearCaseByCourse(params) {
  return request.post('/v4/coursewares/delete/case', {
    ...formatDataSourceHandle(params, { includeCleanerFilter: true })
  });
}

export function resetCaseByCourse(params) {
  return request.post('/v4/coursewares/recovery/case', {
    ...formatDataSourceHandle({ ...params, searchDelete: true }, { includeCleanerFilter: true })
  });
}

// 根据课题获取已删除的案件
export function getCaseByCourse(params) {
  return request.post('/nlp/case/course/recyle/data', params);
}

// 获取筛选弹框法律下拉
export function getLawTree() {
  return request.get('/nlp/case/law/tree');
}

// 获取筛选弹框详细法律法规内容
export function getFilterLaw(params) {
  return request.post('/nlp/case/law/search', params);
}

export function updateAfterDataChange(params) {
  return request.post('/nlp/case/create/label/index', params);
}

export function bacthSetFieldTag(params) {
  return request.post('/v4/coursewares/search/mark', {
    ...formatDataSourceHandle(params, { includeCleanerFilter: true })
  });
}

export function downCourseReport(courseId) {
  const { location } = window;
  const url = `${location.origin}${
    location.pathname
  }#/report?courseware_id=${courseId}&export_token=${encodeURIComponent(store.getters.token)}`;
  return request.post('/nlp/download/report', { url, coursewareId: courseId }, { responseType: 'blob' });
}

export function downMetadata(params) {
  return request.post('/nlp/download/metadata', params);
}

export function downMetadata2(params) {
  return request.post('/v4/coursewares/export', params, { responseType: 'blob' });
}

export function downloadChartImg(courseId, chartId) {
  const { location } = window;
  const url = `${location.origin}${
    location.pathname
  }#/report?courseware_id=${courseId}&export_token=${encodeURIComponent(store.getters.token)}`;
  return request.post(
    '/nlp/download/collection',
    {
      url,
      chart_id: chartId
    },
    { responseType: 'blob' }
  );
}

export function getUserByKeyword(keyword) {
  return request.post('/nlp/analysis/chart/search/phone', { tel: keyword });
}

export function getCourseTogether(coursewareId) {
  return request.post('/nlp/analysis/chart/search/together', { coursewareId });
}

export function inviteTogether({ userId, coursewareId, limit }) {
  return request.post('/nlp/analysis/chart/invite/together', { userId, coursewareId, limit, groupName: '' });
}

export function delTogether(coursewareId, id) {
  return request.post('/nlp/analysis/chart/delete/together', { coursewareId, inviteIds: [id] });
}

export function updTogetherAuth(params) {
  return request.post('/nlp/analysis/chart/course/together/limit', params);
}

export function getTogetherRecord(coursewareId) {
  return request.post('/nlp/analysis/chart/user/records', { coursewareId });
}

export function getInviteLink(params) {
  return request.post('/nlp/analysis/chart/invite/link', {
    limit_time: dayUnixValue,
    ...params
  });
}

export function joinCourseByLink(id) {
  return request.post(`/nlp/analysis/chart/invite/link/${id}`, {});
}

export function getField(id) {
  return request.post(`/nlp/analysis/chart/course/label/detail/${id}`, {});
}

export async function getFields(coursewareId) {
  const result = await request.post('/nlp/analysis/chart/course/label/list ', { coursewareId });
  return sortBy(initColumns(result.hits.hits), 'index_sort');
}

export function updFieldSort(params) {
  return request.post('/nlp/analysis/chart/course/label/sort', params);
}

export function addField(id, params) {
  return request.post('/nlp/analysis/chart/course/label/add', { id, params });
}

export function updField(id, params) {
  return request.post('/nlp/analysis/chart/course/label/update', { id, params });
}

export async function transformNumFieldValue(coursewareId, labelId, unitOld, unitNew) {
  try {
    await request.post('/nlp/analysis/label/changeunit/update/drtag', { coursewareId, labelId, unitOld, unitNew });
  } catch (err) {
    reporter.log(err);
  }
}

export function delField(id) {
  return request.post('/nlp/analysis/chart/course/label/update', { id, params: { is_delete: 1 } });
}

export function collect(courseId, id, groupId) {
  return request.post('/nlp/case/analysis/collect/add', {
    coursewareId: courseId,
    caseIds: [id],
    groupId
  });
}

export function unCollect(courseId, id) {
  return request.post('/nlp/case/analysis/collect/del', {
    coursewareId: courseId,
    caseId: id
  });
}

export function getCollectGroup(courseId) {
  return request.post('/nlp/case/analysis/collect/catalogue', { coursewareId: courseId });
}

export function updCollectGroup(params) {
  return request.post('/nlp/case/favorite/operation', params);
}

export async function getTaskDetail(courseId) {
  const result = await request.post('/nlp/wenshu/doc/task/list', { coursewareId: courseId });
  return result[0];
}

export function createImportTask(courseId, file) {
  const formData = new FormData();
  formData.append('params', JSON.stringify({ coursewareId: courseId }));
  formData.append('file', file);
  formData.append('url', '/nlp/wenshu/doc/file');
  return request.post('/nlp/import/batch', formData);
}

export function getTaskCaseList(courseId, taskId, type, page = 1, size = 10) {
  return request.post('/nlp/wenshu/doc/data/list', {
    coursewareId: courseId,
    task_id: taskId,
    duplicate_rule: type,
    page,
    size
  });
}

export function cancelTaskStatus(taskId) {
  return request.post('/nlp/wenshu/doc/task/update', { id: taskId, params: { status: 'fail' } });
}

export function confirmTaskImport(taskId, type) {
  return request.post('/nlp/wenshu/load/databases', {
    taskId,
    duplicate_rule: type
  });
}

export function mulitAddFields(params) {
  return request.post('/nlp/analysis/label/course/tag/curd', params);
}

export function fetchRecommendSentence(coursewareId, labelId) {
  return request.post('/nlp/analysis/recommend/label/sentence/list', {
    size: 50,
    coursewareId,
    labelId
  });
}

let sysFieldUnitPromise = null;
export function getSysFieldUnit() {
  if (sysFieldUnitPromise) {
    return sysFieldUnitPromise;
  }
  // eslint-disable-next-line no-async-promise-executor
  sysFieldUnitPromise = new Promise(async resolve => {
    try {
      const result = await request.post('/nlp/analysis/label/unit/measurement/list', {});
      const group = {};
      result.forEach(item => {
        if (!group[item._source.deputy_type]) {
          group[item._source.deputy_type] = [];
        }
        group[item._source.deputy_type].push(item._source);
      });
      resolve(group);
    } catch (err) {
      resolve({});
      sysFieldUnitPromise = null;
    }
  });
  return sysFieldUnitPromise;
}

export function checkVersion(courseId, version) {
  return request.get(`/nlp/analysis/esdata/${courseId}/${version}`);
}

export async function getCourseUnitGroups(coursewareId) {
  const result = await request.get(`/v4/data/label/group/courseware/${coursewareId}`);

  result.forEach(groupItem => {
    const fields = (groupItem.allDataLabels || []).map(o => {
      return { _id: o.id, _source: o };
    });
    if (fields.length > 0) {
      delete groupItem.allDataLabels;
    }
    groupItem.fields = initColumns(fields);
    groupItem.isSysGroup = !groupItem.creator || ['预设', '旧版本预设', '刑事预设'].includes(groupItem.type);
    if (!groupItem.desc) {
      const fieldTitles = groupItem.fields.map(o => o.title);
      groupItem.desc = `基于${fieldTitles.join('、')}查看所有标签`;
    }
  });

  return result;
}

export function postCourseUnitGroup(params) {
  return request.post('/v4/data/label/group', params);
}

export function delCourseUnitGroup(groupId, coursewareId) {
  return request.delete(`/v4/data/label/group/${groupId}`, { params: { coursewareId } });
}
